import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Table, Form, Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { 
    getProductPrices, 
    getProductCategories, 
    getFurnitureTypes, 
    getFurnitureTypesByCategory, 
    getFurnitureModelsByTypeId, 
    deleteProductPrice, 
    patchProductPrice, 
    GetProductPricesByTypeIdAndModelId, 
    GetProductPricesByCategoryId, 
    createFavorite, 
    deleteFavorite, 
    updateProductPicture,
    updateCategoryName,
    updateTypeName,
    updateModelName,
    updateColorName,
    updateSizeName,
    logError 
} from '../api';
import { isPropietarioOrAdminOrGestionOrEspectador } from '../services/AuthService';
import DeleteConfirmationModal from './Global/DeleteConfirmationModal';
import { ProductEditorModal } from './ProductEditor';
import Pagination from './Global/Pagination';
import DisabledToggle from './ui/DisabledToggle';
import DisabledRowStyles from './ui/DisabledRowStyles';
import SortableHeader from './ui/SortableHeader';
import { useDisabledToggle } from '../hooks/useDisabledToggle';
import { useSorting } from '../hooks/useSorting';
import { getDisabledRowClass } from '../utils/disabledUtils';
import useFilterPersistence from '../hooks/useFilterPersistence';
import ProductImageManager from './Global/ProductImageManager';
import FavoriteDropdown from './Global/FavoriteDropdown';
import InlineEdit from './Global/InlineEdit';
import TotalCount from './Global/TotalCount';

const ProductList = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const canEdit = isPropietarioOrAdminOrGestionOrEspectador();
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [showEditorModal, setShowEditorModal] = useState(false);
    const [editorProduct, setEditorProduct] = useState(null);
    const [editorMode, setEditorMode] = useState('create');

    // Filtros
    const [productCategories, setProductCategories] = useState([]);
    const [furnitureTypes, setFurnitureTypes] = useState([]);
    const [furnitureModels, setFurnitureModels] = useState([]);
    const [productFilters, setProductFilters] = useFilterPersistence('filters_products', { selectedCategory: '', selectedType: '', selectedModel: '' });
    const { selectedCategory, selectedType, selectedModel } = productFilters;
    const setSelectedCategory = (val) => setProductFilters({ ...productFilters, selectedCategory: val, selectedType: '', selectedModel: '' });
    const setSelectedType = (val) => setProductFilters({ ...productFilters, selectedType: val, selectedModel: '' });
    const setSelectedModel = (val) => setProductFilters({ ...productFilters, selectedModel: val });
    const clearingRef = useRef(false);
    const isMountedRef = useRef(false);

    // Paginación
    const [pagination, setPagination] = useFilterPersistence('pagination_products', { page: 1, pageSize: 20 });
    const currentPage = pagination.page;
    const productsPerPage = pagination.pageSize;
    
    const [totalCount, setTotalCount] = useState(0);
    const { orderBy, orderDesc, handleSort, getSortIcon } = useSorting();

    const { includeDisabled, setIncludeDisabled } = useDisabledToggle();
    useEffect(() => {
        const init = async () => {
            await loadCategories();
            if (selectedCategory) {
                await loadTypesByCategory(selectedCategory);
            } else {
                await loadTypes();
            }
            if (selectedType) {
                await loadModels(selectedType);
            }
            isMountedRef.current = true;
        };
        init();
    }, []);

    // Cargar tipos cuando cambia la categoría
    useEffect(() => {
        if (!isMountedRef.current) return;
        if (selectedCategory) {
            clearingRef.current = true;
            setFurnitureModels([]);
            loadTypesByCategory(selectedCategory).finally(() => {
                clearingRef.current = false;
                loadProducts(selectedCategory, '', '');
            });
        } else {
            loadTypes();
            setFurnitureModels([]);
        }
    }, [selectedCategory]);

    // Cargar modelos cuando cambia el tipo
    useEffect(() => {
        if (!isMountedRef.current) return;
        if (selectedType && selectedCategory) {
            clearingRef.current = true;
            loadModels(selectedType).finally(() => {
                clearingRef.current = false;
                loadProducts(selectedCategory, selectedType, '');
            });
        } else if (!selectedType) {
            setFurnitureModels([]);
        }
    }, [selectedType, selectedCategory]);

    // Cargar productos cuando cambian los filtros
    useEffect(() => {
        if (!isMountedRef.current) return;
        setPagination(prev => ({ ...prev, page: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, selectedType, selectedModel, includeDisabled]);

    useEffect(() => {
        if (!clearingRef.current) {
            loadProducts();
        }
    }, [currentPage, productsPerPage, selectedCategory, selectedType, selectedModel, includeDisabled, orderBy, orderDesc]);

    const loadCategories = async () => {
        try {
            const data = await getProductCategories();
            setProductCategories(data);
        } catch (error) {
            logError(error);
        }
    };

    const loadTypes = async () => {
        try {
            const data = await getFurnitureTypes();
            setFurnitureTypes(data);
        } catch (error) {
            logError(error);
        }
    };

    const loadTypesByCategory = async (categoryId) => {
        try {
            const data = await getFurnitureTypesByCategory(categoryId);
            setFurnitureTypes(data);
        } catch (error) {
            logError(error);
        }
    };

    const loadModels = async (typeId) => {
        try {
            const data = await getFurnitureModelsByTypeId(typeId);
            setFurnitureModels(data);
        } catch (error) {
            logError(error);
        }
    };

    const loadProducts = async (overrideCategory = null, overrideType = null, overrideModel = null) => {
        setLoading(true);
        try {
            const effectiveIncludeDisabled = canEdit ? includeDisabled : false;
            const category = overrideCategory !== null ? overrideCategory : selectedCategory;
            const type = overrideType !== null ? overrideType : selectedType;
            const model = overrideModel !== null ? overrideModel : selectedModel;
            let data;

            if (category && !type) {
                data = await GetProductPricesByCategoryId(
                    category, null, null, currentPage, productsPerPage, 
                    effectiveIncludeDisabled, orderBy, orderDesc
                );
            } else if (type) {
                data = await GetProductPricesByTypeIdAndModelId(
                    type, model || null, currentPage, productsPerPage, 
                    effectiveIncludeDisabled, orderBy, orderDesc
                );
            } else {
                data = await getProductPrices(
                    currentPage, productsPerPage, effectiveIncludeDisabled, orderBy, orderDesc
                );
            }

            setProducts(data.items || []);
            setTotalCount(data.totalCount || 0);
        } catch (error) {
            logError(error);
        } finally {
            setLoading(false);
        }
    };
    
    const handleCreate = () => {
        setEditorProduct(null);
        setEditorMode('create');
        setShowEditorModal(true);
    };

    const handleEdit = (product) => {
        setEditorProduct(product);
        setEditorMode('edit');
        setShowEditorModal(true);
    };

    const handleDuplicate = (product) => {
        setEditorProduct(product);
        setEditorMode('duplicate');
        setShowEditorModal(true);
    };

    const handleEditorSave = () => {
        loadProducts();
        setShowEditorModal(false);
    };

    const handleDelete = async (id) => {
        try {
            await deleteProductPrice(id);
            setProducts(prev => prev.filter(product => product.id !== id));
            setShowDeleteModal(false);
        } catch (error) {
            logError(error);
        }
    };

    const clearFilters = () => {
        setProductFilters({ selectedCategory: '', selectedType: '', selectedModel: '' });
    };

    const handleToggleStatus = async (productId, currentStatus) => {
        try {
            await patchProductPrice(productId, { id: productId, disabled: !currentStatus });
            setProducts(prev => 
                prev.map(product => 
                    product.id === productId 
                        ? { ...product, disabled: !currentStatus }
                        : product
                )
            );
        } catch (error) {
            logError(error);
        }
    };

    const handleToggleFavorite = async (entityType, entity) => {
        try {
            const entityTypeMap = {
                'category': 0, 'type': 1, 'model': 2, 'color': 3, 'size': 4
            };

            if (entity.isFavorite) {
                await deleteFavorite(entity.favoriteId || entity.id);
            } else {
                await createFavorite({
                    entityTypeValueId: entityTypeMap[entityType],
                    entityReferenceId: entity.id
                });
            }
            
            setProducts(prev => 
                prev.map(p => ({
                    ...p,
                    [entityType]: {
                        ...p[entityType],
                        isFavorite: p[entityType].id === entity.id ? !p[entityType].isFavorite : p[entityType].isFavorite
                    }
                }))
            );
        } catch (error) {
            logError(error);
        }
    };

    const handleImageChange = async (productId, pictureBase64) => {
        try {
            await updateProductPicture(productId, pictureBase64);
            setProducts(prev => 
                prev.map(product => 
                    product.id === productId 
                        ? { ...product, pictureBase64 }
                        : product
                )
            );
        } catch (error) {
            logError(error);
        }
    };

    const handleNameEdit = async (id, newName, type, fieldName) => {
        try {
            switch (type) {
                case 'category':
                    await updateCategoryName(id, newName);
                    break;
                case 'type':
                    await updateTypeName(id, newName);
                    break;
                case 'model':
                    await updateModelName(id, newName);
                    break;
                case 'color':
                    await updateColorName(id, newName);
                    break;
                case 'size':
                    await updateSizeName(id, newName);
                    break;
            }
            
            // Actualizar el producto en la lista
            setProducts(prev => 
                prev.map(product => 
                    product[type]?.id === id 
                        ? { 
                            ...product, 
                            [type]: { 
                                ...product[type], 
                                [fieldName]: newName 
                            } 
                        }
                        : product
                )
            );
        } catch (error) {
            logError(error);
            throw error;
        }
    };

    return (
        <>
            <DisabledRowStyles />
            <Container fluid className="my-4">
                <Row className="mb-4">
                    <Col xs={12} md={6}>
                        <h1>Productos</h1>
                    </Col>
                    <Col xs={12} md={6} className="d-flex justify-content-end align-items-center">
                        {canEdit && (
                            <DisabledToggle 
                                includeDisabled={includeDisabled}
                                onChange={setIncludeDisabled}
                            />
                        )}
                        {canEdit && (
                            <Button variant="primary" onClick={handleCreate}>
                                Nuevo producto
                            </Button>
                        )}
                    </Col>
                </Row>

                {/* Filtros */}
                <Card className="mb-4">
                    <Card.Header>
                        <h6 className="mb-0">
                            <i className="bi bi-funnel me-2"></i>
                            Filtros
                        </h6>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={3}>
                                <FavoriteDropdown
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    options={productCategories.map(cat => ({
                                        id: cat.id,
                                        name: cat.categoryName,
                                        isFavorite: cat.isFavorite
                                    }))}
                                    placeholder="Seleccionar categoría"
                                />
                            </Col>
                            <Col md={3}>
                                <FavoriteDropdown
                                    value={selectedType}
                                    onChange={(e) => setSelectedType(e.target.value)}
                                    options={furnitureTypes.map(type => ({
                                        id: type.id,
                                        name: type.typeName,
                                        isFavorite: type.isFavorite
                                    }))}
                                    placeholder="Seleccionar tipo"
                                    disabled={!selectedCategory}
                                />
                            </Col>
                            <Col md={3}>
                                <FavoriteDropdown
                                    value={selectedModel}
                                    onChange={(e) => setSelectedModel(e.target.value)}
                                    options={furnitureModels.map(model => ({
                                        id: model.id,
                                        name: model.modelName,
                                        isFavorite: model.isFavorite
                                    }))}
                                    placeholder="Seleccionar modelo"
                                    disabled={!selectedType}
                                />
                            </Col>
                            <Col md={3} className="d-flex align-items-end">
                                <Button variant="outline-danger" onClick={clearFilters} className="w-100">
                                    <i className="bi bi-x-circle me-1"></i>
                                    Limpiar Filtros
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

            {loading ? (
                <p>Cargando productos...</p>
            ) : (
                <>
                    {/* Total de resultados */}
                    <div className="d-flex justify-content-end mb-2">
                        <TotalCount totalCount={totalCount} />
                    </div>

                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {canEdit && <SortableHeader column="id" onSort={handleSort} getSortIcon={getSortIcon}>Id</SortableHeader>}
                            <th style={{ width: '80px', textAlign: 'center' }}>Imagen</th>
                            <SortableHeader column="type.category.categoryName" onSort={handleSort} getSortIcon={getSortIcon}>Categoría</SortableHeader>
                            <SortableHeader column="type.typeName" onSort={handleSort} getSortIcon={getSortIcon}>Tipo</SortableHeader>
                            <SortableHeader column="model.modelName" onSort={handleSort} getSortIcon={getSortIcon}>Modelo</SortableHeader>
                            <SortableHeader column="color.colorName" onSort={handleSort} getSortIcon={getSortIcon}>Color</SortableHeader>
                            <SortableHeader column="size.size" onSort={handleSort} getSortIcon={getSortIcon}>Tamaño</SortableHeader>
                            <SortableHeader column="price" onSort={handleSort} getSortIcon={getSortIcon}>Precio</SortableHeader>
                            <SortableHeader column="score" onSort={handleSort} getSortIcon={getSortIcon}>Puntuación</SortableHeader>
                            <SortableHeader column="commissionPercentageModifier" onSort={handleSort} getSortIcon={getSortIcon}>Modificador comisión %</SortableHeader>
                            <SortableHeader column="commissionFixedModifier" onSort={handleSort} getSortIcon={getSortIcon}>Modificador comisión fijo</SortableHeader>
                            {canEdit && <th>Usuario</th>}
                            {canEdit && <th>Acciones</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((product) => (
                            <tr key={product.id} className={getDisabledRowClass(product.disabled)}>
                                {canEdit && <td>{product.id}</td>}
                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                    <ProductImageManager
                                        pictureBase64={product.pictureBase64}
                                        onImageChange={canEdit ? (pictureBase64) => handleImageChange(product.id, pictureBase64) : null}
                                        productId={product.id}
                                        readOnly={!canEdit}
                                    />
                                </td>
                                <td>
                                    <InlineEdit
                                        value={product.category?.categoryName}
                                        onSave={(newName) => handleNameEdit(product.category.id, newName, 'category', 'categoryName')}
                                        disabled={!canEdit}
                                    />
                                </td>
                                <td>
                                    <div className="d-flex align-items-center">
                                        {canEdit && (
                                            <Button
                                                variant={product.type.isFavorite ? "warning" : "outline-warning"}
                                                size="sm"
                                                onClick={() => handleToggleFavorite('type', product.type)}
                                                className="me-1"
                                            >
                                                {product.type.isFavorite ? "★" : "☆"}
                                            </Button>
                                        )}
                                        <InlineEdit
                                            value={product.type.typeName}
                                            onSave={(newName) => handleNameEdit(product.type.id, newName, 'type', 'typeName')}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div className="d-flex align-items-center">
                                        {canEdit && (
                                            <Button
                                                variant={product.model.isFavorite ? "warning" : "outline-warning"}
                                                size="sm"
                                                onClick={() => handleToggleFavorite('model', product.model)}
                                                className="me-1"
                                            >
                                                {product.model.isFavorite ? "★" : "☆"}
                                            </Button>
                                        )}
                                        <InlineEdit
                                            value={product.model.modelName}
                                            onSave={(newName) => handleNameEdit(product.model.id, newName, 'model', 'modelName')}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div className="d-flex align-items-center">
                                        {canEdit && (
                                            <Button
                                                variant={product.color.isFavorite ? "warning" : "outline-warning"}
                                                size="sm"
                                                onClick={() => handleToggleFavorite('color', product.color)}
                                                className="me-1"
                                            >
                                                {product.color.isFavorite ? "★" : "☆"}
                                            </Button>
                                        )}
                                        <InlineEdit
                                            value={product.color.colorName}
                                            onSave={(newName) => handleNameEdit(product.color.id, newName, 'color', 'colorName')}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div className="d-flex align-items-center">
                                        {canEdit && (
                                            <Button
                                                variant={product.size.isFavorite ? "warning" : "outline-warning"}
                                                size="sm"
                                                onClick={() => handleToggleFavorite('size', product.size)}
                                                className="me-1"
                                            >
                                                {product.size.isFavorite ? "★" : "☆"}
                                            </Button>
                                        )}
                                        <InlineEdit
                                            value={product.size.size}
                                            onSave={(newName) => handleNameEdit(product.size.id, newName, 'size', 'size')}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                </td>
                                <td>{product.price}€</td>
                                <td>{product.score || 0}</td>
                                <td data-label="Commission">
                                    {`${(product.commissionPercentageModifier).toLocaleString(undefined, {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2
                                    })}%`}
                                </td>
                                <td data-label="CommissionFixed">
                                    {`${(product.commissionFixedModifier || 0).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}€`}
                                </td>
                                {canEdit && <td>{product.user?.username || ''}</td>}
                                {canEdit && (
                                    <td>
                                        <Button 
                                            variant="outline-primary" 
                                            onClick={() => handleEdit(product)} 
                                            className="me-1" 
                                            size="sm"
                                            title="Editar"
                                        >
                                            <i className="bi bi-pencil"></i>
                                        </Button>
                                        <Button 
                                            variant="outline-info" 
                                            onClick={() => handleDuplicate(product)} 
                                            className="me-1" 
                                            size="sm"
                                            title="Crear similar"
                                        >
                                            <i className="bi bi-copy"></i>
                                        </Button>
                                        <Button
                                            variant={product.disabled ? "outline-success" : "outline-secondary"}
                                            onClick={() => handleToggleStatus(product.id, product.disabled)}
                                            size="sm"
                                            className="me-1"
                                            title={product.disabled ? "Habilitar" : "Deshabilitar"}
                                        >
                                            <i className={`bi bi-${product.disabled ? 'check-circle' : 'x-circle'}`}></i>
                                        </Button>
                                        <Button 
                                            variant="outline-danger" 
                                            onClick={() => {
                                                setProductToDelete(product);
                                                setShowDeleteModal(true);
                                            }} 
                                            size="sm"
                                            title="Borrar"
                                        >
                                            <i className="bi bi-trash"></i>
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
                </>
            )}

                {/* Paginación */}
                <Pagination
                    totalCount={totalCount}
                    currentPage={currentPage}
                    itemsPerPage={productsPerPage}
                    onPageChange={(page) => setPagination({ ...pagination, page })}
                    onItemsPerPageChange={(pageSize) => setPagination({ page: 1, pageSize })}
                />

            {canEdit && (
                <>
                    <ProductEditorModal
                        show={showEditorModal}
                        onHide={() => setShowEditorModal(false)}
                        onSave={handleEditorSave}
                        product={editorProduct}
                        mode={editorMode}
                    />

                    <DeleteConfirmationModal
                        show={showDeleteModal}
                        onHide={() => setShowDeleteModal(false)}
                        onConfirm={() => handleDelete(productToDelete?.id)}
                        message={`¿Estás seguro de que deseas eliminar el producto "${productToDelete?.type?.typeName} ${productToDelete?.model?.modelName}"?`}
                    />
                </>
            )}
            </Container>
        </>
    );
};

export default ProductList;
